import { CompilationPreview } from "@turtlewave/types";
import { useState } from "react";
import { RiMultiImageFill } from "react-icons/ri";
import { Link } from "react-router";

import { compilationDetailPath } from "../../utils/navigation";

interface ContentCardProps {
  compilation: CompilationPreview;
  sizeClassName?: string;
}

export const CompilationCard = ({
  compilation,
  sizeClassName,
}: ContentCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const thumbnails = compilation.content_preview_urls.slice(0, 3);
  const hasDescription = Boolean(compilation.description?.trim());

  return (
    <Link
      to={compilationDetailPath(
        compilation.compilation_id,
        compilation.default_sort,
      )}
      className={`block ${sizeClassName || "w-full"}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setIsPressed(false);
      }}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
    >
      <div
        className={`bg-light relative rounded-3xl p-4 shadow-sm transition-[box-shadow,transform] duration-300 ease-out sm:p-4.5 ${isHovered ? "shadow-xl shadow-blue-950/20" : ""} ${isPressed ? "scale-97" : ""}`}
      >
        {/* Image section */}
        <div className="flex flex-col gap-1 sm:gap-2 lg:gap-3">
          {/* Hero image */}
          <div className="relative aspect-5/3 overflow-hidden rounded-2xl">
            <img
              src={compilation.preview_url ?? undefined}
              alt={compilation.title}
              className="h-full w-full object-cover"
            />
            <div className="shadow-inner-sm pointer-events-none absolute inset-0 rounded-2xl" />
          </div>

          {/* Thumbnails row */}
          <div className="flex flex-row gap-1 sm:gap-2 lg:gap-3">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="aspect-square flex-1 overflow-hidden rounded-xl bg-neutral-200"
              >
                {thumbnails[i] && (
                  <img
                    src={thumbnails[i]}
                    alt={`Preview ${i + 1}`}
                    className="h-full w-full object-cover"
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-2 flex items-center justify-between text-neutral-700 md:mt-4">
          <h3 className="truncate text-lg leading-tight font-semibold">
            {compilation.title}
          </h3>
          <div className="flex items-center gap-1.5">
            <RiMultiImageFill size={20} />
            <span className="text-base font-semibold">
              {compilation.entry_count}
            </span>
          </div>
        </div>

        {/* Hover overlay with description */}
        {hasDescription && (
          <div
            className={`bg-light/80 absolute inset-0 flex items-center justify-center rounded-3xl p-5 backdrop-blur-lg transition-all duration-300 ease-out ${isHovered ? "opacity-100" : "pointer-events-none opacity-0"} `}
          >
            <p className="line-clamp-3 text-center text-lg font-medium text-pretty text-neutral-700">
              {compilation.description}
            </p>
          </div>
        )}
      </div>
    </Link>
  );
};
